import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import useInventoryShortages, { normalizeShortagesPage } from "../../hooks/useInventoryShortages";
import useSetItemStripSize from "../../hooks/useSetItemStripSize";
import { isRowInView, getViewPurchase, getShortfall, isPlacedStatus, PARAM_FILTER, PARAM_PAGE, PARAM_PAGE_SIZE, PARAM_SEARCH, PARAM_SHORT, PARAM_LOOKAHEAD, PAGE_SIZE_OPTIONS, LOOKAHEAD_OPTIONS, DEFAULT_LOOKAHEAD_DAYS, SHORTAGES_PAGE_SIZE } from "./shared";

// Shared row-slicing and action state for all three procurement tabs — Overall and New
// render it through ProcurementView.jsx's table shape, Reorder through
// ReorderProcurementView.jsx's prototype-matched shape, but the underlying rules (which rows
// belong to a view, what the suggested quantity is) must be identical everywhere or the three
// views would silently disagree with each other. One hook, three renderers.
//
// Purchase orders are raised from the procurement cart (2026-10-30): a row's "Add to cart" puts
// the medicine in the cart (cart/cartStore.js), and the cart screen is where quantities and the
// vendor are set and the order is placed. There is no row selection or per-row vendor on the table.
export default function useProcurementTable(view) {
    const [searchParams, setSearchParams] = useSearchParams();

    // Server-side pagination + medicine-name search (2026-10-26). Page, search and chip filter
    // live in the URL, so a refresh or a shared link lands on the same slice. The API reads
    // eVital stock for the page with one bulk call — see useInventoryShortages.
    const filterValue = searchParams.get(PARAM_FILTER) || "all";
    const searchValue = searchParams.get(PARAM_SEARCH) || "";
    const pageValue = Math.max(parseInt(searchParams.get(PARAM_PAGE), 10) || 1, 1);
    const sizeParam = parseInt(searchParams.get(PARAM_PAGE_SIZE), 10);
    const pageSizeValue = PAGE_SIZE_OPTIONS.includes(sizeParam) ? sizeParam : SHORTAGES_PAGE_SIZE;
    const shortOnly = searchParams.get(PARAM_SHORT) === "1";
    const lookaheadParam = parseInt(searchParams.get(PARAM_LOOKAHEAD), 10);
    const lookaheadDays = LOOKAHEAD_OPTIONS.includes(lookaheadParam) ? lookaheadParam : DEFAULT_LOOKAHEAD_DAYS;
    // New is committed demand only — the window doesn't apply, so it isn't sent (and the cache
    // key doesn't change) there. The API default is 15, so that isn't sent either.
    const sendLookahead = view !== "new" && lookaheadDays !== DEFAULT_LOOKAHEAD_DAYS;
    const shortageParams = {
        page: pageValue,
        pageSize: pageSizeValue,
        view,
        ...(sendLookahead && { lookaheadDays }),
        ...(filterValue !== "all" && { filter: filterValue }),
        ...(searchValue && { search: searchValue }),
    };

    const { data, isLoading, isFetching, isError, refetch } = useInventoryShortages({ params: shortageParams });
    const pageData = normalizeShortagesPage(data);
    const rows = pageData.rows;
    const setItemStripSize = useSetItemStripSize();

    const [batchesRow, setBatchesRow] = useState(null); // row open in BatchStockModal, or null
    const [receivingRow, setReceivingRow] = useState(null); // row currently open in ReceiveModal, or null
    const [settingStripSizeItemId, setSettingStripSizeItemId] = useState(null);

    // Changing what's searched (or the chip, in the views) starts again from page 1.
    const setSearch = (nextSearch) => {
        const next = new URLSearchParams(searchParams);
        const trimmed = (nextSearch || "").trim();
        if (trimmed) next.set(PARAM_SEARCH, trimmed);
        else next.delete(PARAM_SEARCH);
        next.delete(PARAM_PAGE);
        setSearchParams(next, { replace: true });
    };
    const setLookaheadDays = (days) => {
        const next = new URLSearchParams(searchParams);
        if (days !== DEFAULT_LOOKAHEAD_DAYS) next.set(PARAM_LOOKAHEAD, String(days));
        else next.delete(PARAM_LOOKAHEAD);
        next.delete(PARAM_PAGE);
        setSearchParams(next, { replace: true });
    };
    const setShortOnly = (on) => {
        const next = new URLSearchParams(searchParams);
        if (on) next.set(PARAM_SHORT, "1");
        else next.delete(PARAM_SHORT);
        next.delete(PARAM_PAGE);
        setSearchParams(next, { replace: true });
    };
    const setPageSize = (size) => {
        const next = new URLSearchParams(searchParams);
        if (size !== SHORTAGES_PAGE_SIZE) next.set(PARAM_PAGE_SIZE, String(size));
        else next.delete(PARAM_PAGE_SIZE);
        next.delete(PARAM_PAGE);
        setSearchParams(next, { replace: true });
    };
    const setPage = (nextPage) => {
        const next = new URLSearchParams(searchParams);
        if (nextPage > 1) next.set(PARAM_PAGE, String(nextPage));
        else next.delete(PARAM_PAGE);
        setSearchParams(next);
    };

    const readAt = pageData.readAt || rows[0]?.readAt;

    // §Phase-7-UI — `row.purchase` (and committedDemand/confidentDemand/speculativeDemand)
    // only appear at all when the backend's lapsing flag is on. The paginated API says so
    // directly (it must: an empty page or search has no rows to inspect); an older API is
    // still detected via field presence.
    const lapsingEnabled = pageData.lapsingEnabled ?? (rows.length > 0 && "purchase" in rows[0]);

    // This view's own suggested figure (see getViewPurchase, shared.js) — shown read-only in
    // the table; the modal recomputes it from the medicine's exact stock.
    const getSuggestedQuantity = (row) => getViewPurchase(row, view, lapsingEnabled).tablets ?? row.tabletsToOrder;

    const isRowUnavailable = (row) => row.stockUnknown || row.demandUnknown;

    // This view's slice of the row set — see isRowInView (shared.js). The paginated API
    // already slices by view; this keeps an older, unpaginated API working.
    const viewRows = useMemo(
        () => rows.filter((row) => isRowInView(row, view, lapsingEnabled)),
        [rows, view, lapsingEnabled]
    );

    // "Show only shortages" — client-side, over the rows of the loaded page only (same "short by N"
    // test as the Qty present column). Rows whose stock isn't known aren't shortages.
    const isShortRow = (row) => !isRowUnavailable(row) && getShortfall(row, view, lapsingEnabled) > 0;
    const poolRows = useMemo(
        () => (shortOnly ? viewRows.filter(isShortRow) : viewRows),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [viewRows, shortOnly, view, lapsingEnabled]
    );

    // Chip counts are for this view's FULL set (every page, after search), BEFORE the chip
    // filter narrows it — so "Complete view" always shows the true total. The paginated API
    // counts them server-side (and they follow the look-ahead window); only an older, unpaginated
    // API falls back to counting rows. With "Show only shortages" on, the server can't know which
    // medicines are short (that needs every medicine's stock), so the counts are over the short
    // rows on THIS page instead — `countsAreForPage` lets the screen say so.
    const localChipCounts = useMemo(
        () => ({
            all: poolRows.length,
            pending: poolRows.filter((row) => row.status === "Pending").length,
            placed: poolRows.filter((row) => isPlacedStatus(row.status)).length,
        }),
        [poolRows]
    );
    const chipCounts = shortOnly ? localChipCounts : (pageData.counts || localChipCounts);
    const visibleRows = useMemo(() => {
        if (filterValue === "pending") return poolRows.filter((row) => row.status === "Pending");
        if (filterValue === "placed") return poolRows.filter((row) => isPlacedStatus(row.status));
        return poolRows;
    }, [poolRows, filterValue]);

    // §Phase-7-UI (2026-09-09) — manual strip-size entry. Writes ONLY items.size (see
    // useSetItemStripSize/setItemStripSize) and refetches; this hook never computes or
    // previews a suggestion for the new size itself — calculateShortages recomputes the
    // row server-side on the next read, the one place the formula lives.
    const handleSetStripSize = async (row, size) => {
        setSettingStripSizeItemId(row.itemId);
        try {
            await setItemStripSize.mutateAsync({ itemId: row.itemId, size });
            toast.success(`Strip size set to ${size} for ${row.medicineName || "this medicine"}`);
        } catch (error) {
            toast.error(error?.err || "Error setting strip size");
        } finally {
            setSettingStripSizeItemId(null);
        }
    };

    return {
        isLoading,
        isFetching,
        isError,
        refetch,
        readAt,
        lapsingEnabled,
        isRowUnavailable,
        getSuggestedQuantity,
        viewRows,
        visibleRows,
        chipCounts,
        countsAreForPage: shortOnly,
        filterValue,
        searchParams,
        setSearchParams,
        batchesRow,
        setBatchesRow,
        receivingRow,
        setReceivingRow,
        settingStripSizeItemId,
        handleSetStripSize,
        // Pagination + search (2026-10-26).
        page: pageData.page,
        pageSize: pageData.pageSize,
        total: pageData.total,
        pageCount: pageData.pageCount,
        setPage,
        shortOnly,
        setShortOnly,
        lookaheadDays,
        setLookaheadDays,
        setPageSize,
        searchValue,
        setSearch,
    };
}
